import React, { useState } from 'react';
import Header from '../../ui/Header';
import Sidebar from '../../ui/Sidebar';
import Icon from '../../AppIcon';
import Button from '../../ui/Button';
import PerformanceOverview from './components/PerformanceOverview';
import EarningsChart from './components/EarningsChart';
import ReferralStatusTable from './components/ReferralStatusTable';
import AchievementsBadges from './components/AchievementsBadges';
import LeaderboardRankings from './components/LeaderboardRankings';
import PayoutManagement from './components/PayoutManagement';

const ReferralPerformanceCenter = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [dateRange, setDateRange] = useState('30days');

  // Mock data for performance metrics
  const performanceData = {
    totalReferrals: 247,
    successfulMatches: 89,
    totalEarnings: 45750,
    conversionRate: 36.0,
    activePrograms: 12,
    pendingPayouts: 8500
  };

  // Mock data for earnings chart
  const earningsData = [
  { month: "Jan", earnings: 3200, commissions: 2800 },
  { month: "Feb", earnings: 4100, commissions: 3600 },
  { month: "Mar", earnings: 3800, commissions: 3200 },
  { month: "Apr", earnings: 5200, commissions: 4500 },
  { month: "May", earnings: 4900, commissions: 4200 },
  { month: "Jun", earnings: 6100, commissions: 5400 },
  { month: "Jul", earnings: 5800, commissions: 5100 },
  { month: "Aug", earnings: 7200, commissions: 6300 },
  { month: "Sep", earnings: 6800, commissions: 5900 },
  { month: "Oct", earnings: 8100, commissions: 7200 },
  { month: "Nov", earnings: 7500, commissions: 6800 }];


  // Mock data for referrals
  const referralsData = [
  {
    id: "ref-001",
    startupName: "TechFlow Solutions",
    industry: "AI/ML",
    corporateProgram: "Innovation Accelerator 2024",
    corporateName: "Microsoft Ventures",
    status: "completed",
    commission: 5000,
    commissionType: "Success Fee",
    date: "Nov 10, 2024",
    timeAgo: "4 days ago"
  },
  {
    id: "ref-002",
    startupName: "GreenEnergy Dynamics",
    industry: "CleanTech",
    corporateProgram: "Sustainability Challenge",
    corporateName: "Shell Energy",
    status: "matched",
    commission: 3500,
    commissionType: "Milestone",
    date: "Nov 8, 2024",
    timeAgo: "6 days ago"
  },
  {
    id: "ref-003",
    startupName: "HealthTech Innovations",
    industry: "Healthcare",
    corporateProgram: "Digital Health Initiative",
    corporateName: "Johnson & Johnson",
    status: "reviewing",
    commission: 4200,
    commissionType: "Success Fee",
    date: "Nov 5, 2024",
    timeAgo: "9 days ago"
  },
  {
    id: "ref-004",
    startupName: "FinanceAI Corp",
    industry: "FinTech",
    corporateProgram: "Banking Innovation Lab",
    corporateName: "JPMorgan Chase",
    status: "pending",
    commission: 6000,
    commissionType: "Performance",
    date: "Nov 3, 2024",
    timeAgo: "11 days ago"
  },
  {
    id: "ref-005",
    startupName: "CyberShield Systems",
    industry: "Cybersecurity",
    corporateProgram: "Security Innovation Program",
    corporateName: "IBM Security",
    status: "rejected",
    commission: 0,
    commissionType: "N/A",
    date: "Oct 28, 2024",
    timeAgo: "17 days ago"
  }];


  // Mock data for achievements
  const achievementsData = [
  {
    id: "ach-001",
    title: "First Success",
    description: "Complete your first successful referral",
    category: "milestones",
    tier: "bronze",
    earnedDate: "Mar 15, 2024",
    reward: "$100 bonus"
  },
  {
    id: "ach-002",
    title: "Quality Scout",
    description: "Maintain 80% success rate for 3 months",
    category: "quality",
    tier: "silver",
    earnedDate: "Jun 22, 2024",
    reward: "Premium badge"
  },
  {
    id: "ach-003",
    title: "Top Performer",
    description: "Rank in top 10 for monthly earnings",
    category: "earnings",
    tier: "gold",
    earnedDate: "Aug 10, 2024",
    reward: "$500 bonus"
  },
  {
    id: "ach-004",
    title: "Network Builder",
    description: "Refer startups from 10 different industries",
    category: "referrals",
    tier: "silver",
    earnedDate: "Sep 5, 2024",
    reward: "Industry expert status"
  },
  {
    id: "ach-005",
    title: "Consistency King",
    description: "Complete referrals for 6 consecutive months",
    category: "milestones",
    tier: "gold",
    earnedDate: "Oct 1, 2024",
    reward: "Exclusive access"
  },
  {
    id: "ach-006",
    title: "Speed Demon",
    description: "Complete 10 referrals in one week",
    category: "performance",
    tier: "platinum",
    earnedDate: "Oct 20, 2024",
    reward: "$1000 bonus"
  }];


  // Mock data for leaderboard
  const leaderboardData = [
  {
    id: "user-001",
    name: "Sarah Chen",
    location: "San Francisco, CA",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_11d7bc7ea-1762273732946.png",
    avatarAlt: "Professional Asian woman with long black hair in business attire smiling at camera",
    earnings: 125000,
    referrals: 340,
    success_rate: 92,
    quality_score: 9.8,
    subtitle: "Tech Specialist",
    trend: 15
  },
  {
    id: "user-002",
    name: "Marcus Rodriguez",
    location: "Austin, TX",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1317ce20a-1762248863496.png",
    avatarAlt: "Hispanic man with short dark hair and beard in navy blue shirt smiling professionally",
    earnings: 98500,
    referrals: 285,
    success_rate: 88,
    quality_score: 9.5,
    subtitle: "Healthcare Focus",
    trend: 8
  },
  {
    id: "user-current",
    name: "Alex Thompson",
    location: "Boston, MA",
    avatar: "https://images.unsplash.com/photo-1623184663110-89ba5b565eb6",
    avatarAlt: "Caucasian man with brown hair in light blue button-down shirt smiling confidently",
    earnings: 45750,
    referrals: 247,
    success_rate: 36,
    quality_score: 8.2,
    subtitle: "Multi-Industry",
    trend: 12
  }];


  const currentUser = { id: "user-current" };

  // Mock data for payout management
  const payoutData = {
    availableBalance: 8500,
    monthlyEarnings: 7500,
    pendingPayouts: [
    { id: "payout-001", amount: 2500, date: "Nov 15, 2024" },
    { id: "payout-002", amount: 3200, date: "Nov 20, 2024" }],

    recentPayouts: [
    {
      id: "payout-003",
      amount: 5000,
      method: "bank_transfer",
      status: "completed",
      date: "Nov 1, 2024",
      timeAgo: "13 days ago",
      fee: 0
    },
    {
      id: "payout-004",
      amount: 3500,
      method: "paypal",
      status: "completed",
      date: "Oct 15, 2024",
      timeAgo: "30 days ago",
      fee: 105
    },
    {
      id: "payout-005",
      amount: 4200,
      method: "bank_transfer",
      status: "processing",
      date: "Nov 12, 2024",
      timeAgo: "2 days ago",
      fee: 0
    }]

  };

  const tabs = [
  { id: 'overview', label: 'Performance Overview', icon: 'BarChart3' },
  { id: 'referrals', label: 'Referral Tracking', icon: 'Users' },
  { id: 'achievements', label: 'Achievements', icon: 'Award' },
  { id: 'leaderboard', label: 'Leaderboard', icon: 'Trophy' },
  { id: 'payouts', label: 'Payouts', icon: 'DollarSign' }];


  const dateRangeOptions = [
  { value: '7days', label: 'Last 7 Days' },
  { value: '30days', label: 'Last 30 Days' },
  { value: '90days', label: 'Last 90 Days' },
  { value: '1year', label: 'Last Year' }];


  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-8">
            <PerformanceOverview performanceData={performanceData} />
            <EarningsChart earningsData={earningsData} />
          </div>);

      case 'referrals':
        return <ReferralStatusTable referrals={referralsData} />;
      case 'achievements':
        return <AchievementsBadges achievements={achievementsData} userStats={performanceData} />;
      case 'leaderboard':
        return <LeaderboardRankings leaderboardData={leaderboardData} currentUser={currentUser} />;
      case 'payouts':
        return <PayoutManagement payoutData={payoutData} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />

      <main className={`transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-64'} pt-16`}>
        <div className="p-6 max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">Referral Performance Center</h1>
                <p className="text-muted-foreground">
                  Track your referral success, earnings, and performance metrics in real-time
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="flex items-center bg-muted rounded-lg p-1">
                  {dateRangeOptions?.map((option) =>
                  <button
                    key={option?.value}
                    onClick={() => setDateRange(option?.value)}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                    dateRange === option?.value ?
                    'bg-background text-foreground shadow-sm' :
                    'text-muted-foreground hover:text-foreground'}`
                    }>

                      {option?.label}
                    </button>
                  )}
                </div>
                
                <Button variant="outline">
                  <Icon name="Download" size={16} className="mr-2" />
                  Export Report
                </Button>
                
                <Button variant="default">
                  <Icon name="Plus" size={16} className="mr-2" />
                  New Referral
                </Button>
              </div>
            </div>

            {/* Quick Stats Bar */}
            <div className="bg-card rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm text-muted-foreground">System Status: Online</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Last Updated: {new Date()?.toLocaleString()}
                  </div>
                </div>
                
                <div className="flex items-center space-x-4 text-sm">
                  <div className="flex items-center space-x-1">
                    <Icon name="TrendingUp" size={16} className="text-green-600" />
                    <span className="text-muted-foreground">Performance: </span>
                    <span className="font-medium text-green-600">+15%</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Target" size={16} className="text-blue-600" />
                    <span className="text-muted-foreground">Goal Progress: </span>
                    <span className="font-medium text-blue-600">78%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="mb-8">
            <div className="border-b border-border">
              <nav className="flex space-x-8 overflow-x-auto">
                {tabs?.map((tab) =>
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-all duration-200 ${
                  activeTab === tab?.id ?
                  'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'}`
                  }>

                    <Icon name={tab?.icon} size={18} />
                    <span>{tab?.label}</span>
                  </button>
                )}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="animate-fade-in">
            {renderTabContent()}
          </div>
        </div>
      </main>
    </div>);

};

export default ReferralPerformanceCenter;